// ============================================================================
// Hiring → Onboarding hand-off modal (Antonela spec §3.4 + HANDOFF CONTRACT).
//
// Opens when a requisition is moved to the Onboarding stage (drag-drop or the
// "Move to Stage" dropdown). Captures who was hired, then seeds an
// employee_onboarding row (canonical checklist) and advances the source
// hiring_requests row to 'Onboarding'. The Onboarding module consumes the row.
//
// This is the ONLY write into employee_onboarding from the hiring module.
// ============================================================================

import { useState } from 'react';
import { X as XIcon } from 'lucide-react';
import {
  createOnboardingFromHire,
  type HandoffInput,
  type HiringRequestRow,
} from './hooks/use-hiring-data';
import { overlayClickClose } from '../../shared/overlay-close';
import { errorMessage } from '../../shared/errors';

// 2026-06-02 (Antonela H3): "Partner" and "Subcontractor" mean the same thing.
// The category selector is consolidated to a single Subcontractor option.
const TYPES: HandoffInput['hireType'][] = ['W2', 'Subcontractor'];
const TYPE_LABELS: Record<HandoffInput['hireType'], string> = {
  W2: 'W2 Employee',
  Subcontractor: 'Subcontractor',
};

interface Props {
  row: HiringRequestRow;
  onClose: () => void;
  onCreated: (onboardingId: string, hireName: string) => void;
}

export default function HandoffModal({ row, onClose, onCreated }: Props) {
  const [name, setName] = useState('');
  const [type, setType] = useState<HandoffInput['hireType']>('W2');
  const [startDate, setStartDate] = useState('');
  const [payRate, setPayRate] = useState(
    row.rate_offered != null ? String(row.rate_offered) : '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0 && !!startDate && !submitting;

  async function handleSubmit() {
    if (!canSubmit) {
      setError('Name and start date are required.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const parsedRate = payRate.trim() ? Number(payRate) : null;
      const { onboarding_id } = await createOnboardingFromHire(row, {
        hireName: name.trim(),
        hireType: type,
        startDate,
        payRate:
          parsedRate != null && Number.isFinite(parsedRate) ? parsedRate : null,
      });
      onCreated(onboarding_id, name.trim());
    } catch (err) {
      console.error('[handoff] onboarding create failed', err);
      setError(
        errorMessage(err, 'Could not create onboarding record.'),
      );
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-tcs-scrim/60 p-4"
      {...overlayClickClose(onClose)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl bg-tcs-overlay p-5 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-tcs-text">
              Who was hired for this role?
            </h3>
            <p className="mt-0.5 truncate text-xs text-tcs-text-muted">
              {row.title} · {row.account_name ?? row.account_id ?? '—'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cancel"
            className="rounded-md p-1 text-tcs-text-muted hover:bg-tcs-surface"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 space-y-4">
          <label className="block text-xs">
            <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
              Name *
            </span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              placeholder="New hire's full name"
              className={inputCls}
            />
          </label>

          <div className="text-xs">
            <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
              Type *
            </span>
            <div className="flex flex-wrap gap-2">
              {TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={`rounded-md border px-3 py-2 text-sm font-medium transition ${
                    type === t
                      ? 'border-tcs-inverse bg-tcs-inverse text-tcs-inverse-ink'
                      : 'border-tcs-border bg-tcs-raised text-tcs-text-secondary hover:bg-tcs-bg-muted'
                  }`}
                >
                  {TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <label className="block text-xs">
              <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
                Start date *
              </span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={inputCls}
              />
            </label>
            <label className="block text-xs">
              <span className="mb-1 block font-medium uppercase tracking-wide text-tcs-text-muted">
                Pay rate ($/hr)
              </span>
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={payRate}
                onChange={(e) => setPayRate(e.target.value)}
                placeholder="defaults to rate offered"
                className={inputCls}
              />
            </label>
          </div>
        </div>

        {error && <p className="mt-3 text-xs text-st-danger-fg">{error}</p>}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md border border-tcs-border bg-tcs-raised px-3 py-1.5 text-sm font-medium text-tcs-text-secondary hover:bg-tcs-bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="rounded-md bg-tcs-inverse px-4 py-1.5 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-50"
          >
            {submitting ? 'Creating…' : 'Create Onboarding'}
          </button>
        </div>
      </div>
    </div>
  );
}

const inputCls =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base focus:border-tcs-border-input focus:outline-none';
